import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardHeader } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Select } from '../../components/ui/Select'
import { Modal } from '../../components/ui/Modal'
import { SkeletonListPage } from '../../components/ui/Skeleton'
import { StatusModal, useStatusModal } from '../../components/ui/StatusModal'
import * as categoryService from '../../services/categoryService'
import * as homepageSectionsService from '../../services/homepageSectionsService'
import { HomepageSectionCategoryPicker } from '../../components/homepage/HomepageSectionCategoryPicker'
import { useCurrentStore } from '../../context/CurrentStoreContext'
import { getUserFacingErrorMessage } from '../../utils/userFacingError'

const textareaClass =
  'w-full rounded-2xl border border-input bg-background px-4 py-2.5 text-sm text-foreground shadow-sm min-h-[72px] resize-y'

function toDatetimeLocal(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function fromDatetimeLocal(local: string): string | null {
  if (!local.trim()) return null
  const d = new Date(local)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

function statusBadgeClass(status: homepageSectionsService.VisibilityStatus): string {
  switch (status) {
    case 'active_now':
      return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
    case 'scheduled':
      return 'bg-amber-500/15 text-amber-800 dark:text-amber-300'
    case 'expired':
      return 'bg-muted text-muted-foreground'
    case 'disabled':
      return 'bg-destructive/15 text-destructive'
    default:
      return 'bg-muted text-muted-foreground'
  }
}

function formatSchedule(row: homepageSectionsService.HomepageSectionRecord): string {
  if (!row.startDate && !row.endDate) return 'Always on'
  const start = row.startDate ? new Date(row.startDate).toLocaleString() : '—'
  const end = row.endDate ? new Date(row.endDate).toLocaleString() : '—'
  return `${start} → ${end}`
}

function manageProductsHref(sectionId: number): string {
  return `/homepage-sections/${encodeURIComponent(String(sectionId))}/products`
}

type FormState = {
  title: string
  slug: string
  subtitle: string
  sectionType: homepageSectionsService.HomepageSectionRecord['sectionType']
  sourceType: homepageSectionsService.HomepageSectionRecord['sourceType']
  categoryId: string
  startDate: string
  endDate: string
  isActive: boolean
}

function emptyForm(): FormState {
  return {
    title: '',
    slug: '',
    subtitle: '',
    sectionType: 'product_grid',
    sourceType: 'manual_products',
    categoryId: '',
    startDate: '',
    endDate: '',
    isActive: true,
  }
}

export default function HomepageSectionsPage() {
  const { openSuccess, openError, closeStatusModal, statusModalProps } = useStatusModal()
  const { currentStoreId: storeId } = useCurrentStore()
  const [items, setItems] = useState<homepageSectionsService.HomepageSectionRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<FormState>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [categories, setCategories] = useState<categoryService.CategoryRow[]>([])

  const loadSections = useCallback(async () => {
    if (storeId == null) return
    setLoading(true)
    setError(null)
    try {
      const res = await homepageSectionsService.listSections(storeId)
      setItems(res.items)
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Failed to load sections'))
    } finally {
      setLoading(false)
    }
  }, [storeId])

  useEffect(() => {
    void loadSections()
  }, [loadSections])

  useEffect(() => {
    setModalOpen(false)
    setEditingId(null)
  }, [storeId])

  useEffect(() => {
    if (storeId == null) return
    categoryService
      .fetchCategories({ page: 1, limit: 100 })
      .then((res) => setCategories(res.items))
      .catch(() => setCategories([]))
  }, [storeId])

  const openCreate = () => {
    setEditingId(null)
    setForm(emptyForm())
    setModalOpen(true)
  }

  const openEdit = (row: homepageSectionsService.HomepageSectionRecord) => {
    setEditingId(row.id)
    setForm({
      title: row.title ?? '',
      slug: row.slug ?? '',
      subtitle: row.subtitle ?? '',
      sectionType: row.sectionType,
      sourceType: row.sourceType,
      categoryId: row.categoryId != null ? String(row.categoryId) : '',
      startDate: toDatetimeLocal(row.startDate),
      endDate: toDatetimeLocal(row.endDate),
      isActive: row.isActive,
    })
    setModalOpen(true)
  }

  const buildPayload = (): homepageSectionsService.HomepageSectionPayload => {
    const start = fromDatetimeLocal(form.startDate)
    const end = fromDatetimeLocal(form.endDate)
    if (start && end && new Date(end).getTime() <= new Date(start).getTime()) {
      throw new Error('End date must be after start date.')
    }
    if (form.sourceType === 'category' && !form.categoryId) {
      throw new Error('Choose a category for this section.')
    }
    const payload: homepageSectionsService.HomepageSectionPayload = {
      title: form.title.trim() || null,
      subtitle: form.subtitle.trim() || null,
      ...(form.slug.trim() ? { slug: form.slug.trim() } : {}),
      sectionType: form.sectionType,
      sourceType: form.sourceType,
      categoryId: form.categoryId ? Number(form.categoryId) : null,
      startDate: start,
      endDate: end,
      isActive: form.isActive,
    }
    if (editingId == null && form.sourceType === 'manual_products') {
      payload.manualProducts = []
    }
    return payload
  }

  const handleSave = async () => {
    if (storeId == null) return
    setSaving(true)
    setError(null)
    try {
      const payload = buildPayload()
      if (editingId == null) {
        const created = await homepageSectionsService.createSection(storeId, payload)
        setModalOpen(false)
        await loadSections()
        if (created.sourceType === 'manual_products') {
          openSuccess({
            title: 'Section created',
            description: (
              <span>
                Add products on the{' '}
                <Link
                  to={manageProductsHref(created.id)}
                  className="font-medium text-primary underline-offset-4 hover:underline"
                  onClick={() => closeStatusModal()}
                >
                  Manage Products
                </Link>{' '}
                page.
              </span>
            ),
            autoCloseMs: 0,
          })
        } else {
          openSuccess({
            title: 'Section created',
            description: 'Your homepage section is saved. It will appear when active and scheduled.',
          })
        }
      } else {
        await homepageSectionsService.updateSection(storeId, editingId, payload)
        openSuccess({ title: 'Section updated', description: 'Homepage merchandising changes are saved.' })
        setModalOpen(false)
        await loadSections()
      }
    } catch (e) {
      const msg = getUserFacingErrorMessage(e, 'Save failed')
      setError(msg)
      openError({ title: 'Could not save section', description: msg })
    } finally {
      setSaving(false)
    }
  }

  const toggleActive = async (row: homepageSectionsService.HomepageSectionRecord) => {
    if (storeId == null) return
    try {
      await homepageSectionsService.updateSection(storeId, row.id, { isActive: !row.isActive })
      await loadSections()
    } catch (e) {
      openError({
        title: 'Update failed',
        description: getUserFacingErrorMessage(e, 'Could not update section'),
      })
    }
  }

  const moveSection = async (index: number, direction: -1 | 1) => {
    if (storeId == null) return
    const next = index + direction
    if (next < 0 || next >= items.length) return
    const reordered = [...items]
    const tmp = reordered[index]
    reordered[index] = reordered[next]
    reordered[next] = tmp
    const withOrder = reordered.map((row, i) => ({ id: row.id, sortOrder: i * 10 }))
    try {
      const res = await homepageSectionsService.reorderSections(storeId, withOrder)
      setItems(res.items)
    } catch (e) {
      openError({
        title: 'Reorder failed',
        description: getUserFacingErrorMessage(e, 'Could not reorder sections'),
      })
    }
  }

  const handleDelete = async (row: homepageSectionsService.HomepageSectionRecord) => {
    if (storeId == null || row.isSystemGenerated) return
    if (!window.confirm('Delete this homepage section?')) return
    try {
      await homepageSectionsService.deleteSection(storeId, row.id)
      openSuccess({ title: 'Section deleted', description: 'The section was removed from your homepage.' })
      await loadSections()
    } catch (e) {
      openError({
        title: 'Delete failed',
        description: getUserFacingErrorMessage(e, 'Could not delete section'),
      })
    }
  }

  const editingRow = editingId != null ? items.find((i) => i.id === editingId) : null
  const formReadOnly = Boolean(editingRow?.isSystemGenerated)

  return (
    <div className="space-y-6">
      <StatusModal {...statusModalProps} />
      <Card>
        <CardHeader
          title="Homepage sections"
          description="Merchandising blocks below your hero and trust badges. System sections can be reordered and scheduled; custom sections can be fully edited."
        />
        {error ? <p className="mb-4 text-sm text-destructive">{error}</p> : null}
        <div className="mb-4 flex flex-wrap gap-2">
          <Button type="button" onClick={openCreate} disabled={storeId == null}>
            Add section
          </Button>
        </div>
        {loading ? (
          <SkeletonListPage toolbarFields={0} columns={6} rows={6} />
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full min-w-[800px] text-left text-sm">
              <thead className="border-b border-border bg-muted/40 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Order</th>
                  <th className="px-3 py-2">Title</th>
                  <th className="px-3 py-2">Slug</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Source</th>
                  <th className="px-3 py-2">Products</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Schedule</th>
                  <th className="px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row, index) => (
                  <tr key={row.id} className="border-b border-border/60">
                    <td className="px-3 py-2">
                      <div className="flex gap-1">
                        <Button type="button" variant="secondary" size="sm" disabled={index === 0} onClick={() => moveSection(index, -1)}>
                          ↑
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={index === items.length - 1}
                          onClick={() => moveSection(index, 1)}
                        >
                          ↓
                        </Button>
                      </div>
                    </td>
                    <td className="px-3 py-2 font-medium">
                      {row.title ?? row.systemKey ?? '—'}
                      {row.isSystemGenerated ? (
                        <span className="ml-2 text-xs text-muted-foreground">(system)</span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{row.slug}</td>
                    <td className="px-3 py-2">{row.sectionType}</td>
                    <td className="px-3 py-2">{row.sourceType}</td>
                    <td className="px-3 py-2 tabular-nums text-muted-foreground">
                      {row.sourceType === 'manual_products' ? (row.manualProductIds?.length ?? 0) : '—'}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(row.visibilityStatus)}`}
                      >
                        {row.visibilityStatus.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{formatSchedule(row)}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        <Button type="button" variant="secondary" size="sm" onClick={() => openEdit(row)}>
                          Edit
                        </Button>
                        {row.sourceType === 'manual_products' ? (
                          <Link to={manageProductsHref(row.id)}>
                            <Button type="button" variant="secondary" size="sm">
                              Manage products
                            </Button>
                          </Link>
                        ) : null}
                        <Button type="button" variant="secondary" size="sm" onClick={() => toggleActive(row)}>
                          {row.isActive ? 'Disable' : 'Enable'}
                        </Button>
                        {!row.isSystemGenerated ? (
                          <Button type="button" variant="secondary" size="sm" onClick={() => handleDelete(row)}>
                            Delete
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        open={modalOpen}
        onClose={() => !saving && setModalOpen(false)}
        title={editingId == null ? 'Add homepage section' : 'Edit homepage section'}
        size="lg"
        footer={
          <>
            <Button type="button" variant="secondary" disabled={saving} onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button type="button" disabled={saving} onClick={() => void handleSave()}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      >
        {formReadOnly ? (
          <p className="mb-4 text-sm text-muted-foreground">
            System section: you can change active state, schedule, and order only. Title and product logic are fixed.
          </p>
        ) : null}
        <div className="space-y-4">
          <label className="block space-y-1">
            <span className="text-xs font-medium text-muted-foreground">Title</span>
            <Input
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              disabled={formReadOnly}
            />
          </label>
          <label className="block space-y-1">
            <span className="text-xs font-medium text-muted-foreground">Subtitle</span>
            <textarea
              className={textareaClass}
              value={form.subtitle}
              onChange={(e) => setForm((f) => ({ ...f, subtitle: e.target.value }))}
              disabled={formReadOnly}
            />
          </label>
          {!formReadOnly ? (
            <label className="block space-y-1">
              <span className="text-xs font-medium text-muted-foreground">Collection URL slug</span>
              <Input
                value={form.slug}
                onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                placeholder="for-men"
                autoComplete="off"
              />
              <p className="text-xs text-muted-foreground">
                Storefront link: /collections/your-slug. Leave blank to generate from the title.
              </p>
            </label>
          ) : null}
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block space-y-1">
              <span className="text-xs font-medium text-muted-foreground">Section type</span>
              <Select
                value={form.sectionType}
                disabled={formReadOnly}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    sectionType: e.target.value as FormState['sectionType'],
                  }))
                }
              >
                <option value="product_grid">Product grid</option>
                <option value="carousel">Carousel</option>
                <option value="banner">Banner</option>
                <option value="category_grid">Category grid</option>
              </Select>
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-medium text-muted-foreground">Source</span>
              <Select
                value={form.sourceType}
                disabled={formReadOnly}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    sourceType: e.target.value as FormState['sourceType'],
                  }))
                }
              >
                <option value="manual_products">Manual products</option>
                <option value="category">Category</option>
                <option value="automatic">Automatic</option>
              </Select>
            </label>
          </div>
          {form.sourceType === 'category' && !formReadOnly ? (
            <HomepageSectionCategoryPicker
              categories={categories}
              value={form.categoryId}
              onChange={(categoryId) => setForm((f) => ({ ...f, categoryId }))}
            />
          ) : null}
          {form.sourceType === 'manual_products' && !formReadOnly ? (
            <div className="space-y-3 rounded-xl border border-border bg-muted/20 px-4 py-3">
              <p className="text-sm text-muted-foreground">
                After saving this section, add and reorder products from the Manage Products page.
              </p>
              {editingId != null && storeId != null ? (
                <Link to={manageProductsHref(editingId)}>
                  <Button type="button" variant="secondary" size="sm">
                    Manage products
                  </Button>
                </Link>
              ) : null}
            </div>
          ) : null}
          {form.sourceType === 'automatic' && !formReadOnly ? (
            <p className="rounded-xl border border-border bg-muted/20 px-4 py-3 text-sm text-muted-foreground">
              Automatic sections use store rules (trending, new arrivals, etc.). Choose manual products or a category
              if you want to pick items yourself.
            </p>
          ) : null}
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block space-y-1">
              <span className="text-xs font-medium text-muted-foreground">Start date (optional)</span>
              <Input
                type="datetime-local"
                value={form.startDate}
                onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
              />
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-medium text-muted-foreground">End date (optional)</span>
              <Input
                type="datetime-local"
                value={form.endDate}
                onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
              />
            </label>
          </div>
          <p className="text-xs text-muted-foreground">Leave dates blank to show immediately.</p>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
            />
            Active on storefront
          </label>
        </div>
      </Modal>
    </div>
  )
}
